<script setup lang="ts">
import type { InformationalEventResource } from '@@/types/api';
import { useCardInterest } from '~/composables/use-card-interest';

const props = defineProps<{
  event: InformationalEventResource;
}>();

// A concluded (past) Almanac event gets no interest control — defense in depth
// alongside the feed's today-floor, matching the detail page. Derived once from
// the card's (stable) timing; `props.event` never changes for a keyed card.
const isConcluded = isInformationalEventConcluded(props.event.timing);

// Per-card interest wiring (Almanac). Interest is a NON-transactional engagement
// affordance — the only interactive control an informational card ever gets. No
// pricing / commit / roster is introduced here.
const {
  descriptor: interestDescriptor,
  interestedCount,
  goingCount,
  isPending: isInterestPending,
  snackbar: interestSnackbar,
  snackbarText: interestSnackbarText,
  snackbarColor: interestSnackbarColor,
  setInterest,
} = useCardInterest({
  source: 'almanac',
  eventId: computed(() => props.event.id),
  initialInterested: props.event.interested_count,
  initialGoing: props.event.going_count,
  title: props.event.title,
  kind: props.event.event_kind,
  isConcluded,
});

// `City, ST` line assembled from the (nullable) venue. Omitted entirely when
// neither part is present so the card body doesn't render an empty row.
const locationLine = computed(() => {
  const venue = props.event.venue;
  const parts = [venue?.city, venue?.state].filter(
    (part): part is string => !!part,
  );
  return parts.join(', ');
});
</script>

<template>
  <v-card
    :to="`/discover/${event.id}`"
    variant="outlined"
    rounded="lg"
    class="informational-event-card"
  >
    <div class="informational-event-card__cover">
      <DiscoverInformationalEventCover
        :id="event.id"
        :title="event.title"
        :event-kind="event.event_kind"
        :city="event.venue?.city"
        :state="event.venue?.state"
      />
      <div class="informational-event-card__badge d-flex justify-end pa-2">
        <DiscoverThirdPartyBadge variant="elevated" />
      </div>
    </div>

    <v-card-title class="text-subtitle-1 font-weight-bold pb-1">
      {{ event.title }}
    </v-card-title>

    <v-card-subtitle class="pb-2">
      <v-icon size="small" class="mr-1">mdi-calendar</v-icon>
      <!-- Rendered verbatim from the backend timing tagged union — never
           re-format or derive a datetime on the frontend. -->
      {{ event.timing.display }}
      <template v-if="locationLine">
        <br />
        <v-icon size="small" class="mr-1">mdi-map-marker</v-icon>
        {{ locationLine }}
      </template>
    </v-card-subtitle>

    <v-card-text v-if="event.organizer?.display_name" class="pt-0">
      <div class="text-body-2 text-medium-emphasis">
        {{ event.organizer.display_name }}
      </div>
    </v-card-text>

    <v-card-text class="pt-0">
      <!-- Social proof: always shown, reflects optimistic interest toggles. -->
      <EventsSocialProofCounts
        :interested-count="interestedCount"
        :going-count="goingCount"
        class="mb-2"
      />

      <!-- Interest toggle. Wrapped in a click guard so tapping the control never
           triggers the card's :to navigation to the detail page. -->
      <div
        v-if="interestDescriptor.visible"
        class="informational-event-card__interest"
        @click.stop.prevent
      >
        <EventsEventInterestControl
          :descriptor="interestDescriptor"
          :event-id="event.id"
          :pending="isInterestPending"
          @select="setInterest"
        />
      </div>
    </v-card-text>
  </v-card>

  <v-snackbar
    v-model="interestSnackbar"
    :color="interestSnackbarColor"
    :timeout="4000"
    location="bottom"
  >
    {{ interestSnackbarText }}
  </v-snackbar>
</template>

<style scoped>
.informational-event-card {
  transition: box-shadow 0.2s ease;
  text-decoration: none !important;
}
.informational-event-card:hover {
  box-shadow: 0 4px 12px rgb(0 0 0 / 12%);
}
.informational-event-card__cover {
  position: relative;
}
.informational-event-card__badge {
  position: absolute;
  inset: 0 0 auto 0;
}
</style>
